import { useCallback, useRef, useState } from 'react'

import type { Query } from '@tanstack/react-query'

import {
    BACK_OFF_RATE,
    POLL_INTERVAL,
    POLL_INTERVAL_MAX,
    POLL_TIMEOUT,
} from 'const/polling'

export interface UsePollingWhilePendingOptions<T, TData = unknown> {
    getResults: (queryData: TData | undefined) => T[]
    isPending: (item: T) => boolean
    /** Initial interval in ms; with back-off this is the first delay. Defaults to POLL_INTERVAL. */
    intervalMs?: number
    /** Max interval in ms when using back-off. If provided with backOffRate, interval increases each poll. Defaults to POLL_INTERVAL_MAX. */
    intervalMaxMs?: number
    /** Rate to increase interval each poll (e.g. 0.25 => 25% increase), capped at intervalMaxMs. Defaults to BACK_OFF_RATE. */
    backOffRate?: number
    /** Stop polling after this many ms. Defaults to POLL_TIMEOUT. */
    timeoutMs?: number
}

/** RefetchInterval function compatible with useQuery's refetchInterval option */
export type PollingRefetchInterval<TData = unknown, TError = Error> = (
    query: Query<TData, TError, TData, readonly unknown[]>
) => number | false

export interface UsePollingWhilePendingResult<TData = unknown, TError = Error> {
    refetchInterval: PollingRefetchInterval<TData, TError>
    pollTimeoutReached: boolean
    /**
     * Clears the timeout flag and elapsed-time tracker so the next poll
     * starts a fresh window. Call this when the consumer's reason to poll
     * goes away (e.g. drawer closes) so reopening doesn't reuse a stale
     * start time and immediately re-time out.
     */
    reset: () => void
}

export function usePollingWhilePending<T, TData = unknown, TError = Error>({
    getResults,
    isPending,
    intervalMs = POLL_INTERVAL,
    intervalMaxMs = POLL_INTERVAL_MAX,
    backOffRate = BACK_OFF_RATE,
    timeoutMs = POLL_TIMEOUT,
}: UsePollingWhilePendingOptions<T, TData>): UsePollingWhilePendingResult<
    TData,
    TError
> {
    const pollingStartedAtRef = useRef<number | null>(null)
    const pollTimeoutReachedRef = useRef(false)
    const currentIntervalRef = useRef(intervalMs)
    const [pollTimeoutReached, setPollTimeoutReached] = useState(false)

    const useBackOff = intervalMaxMs > intervalMs

    const refetchInterval = useCallback(
        (
            query: Query<TData, TError, TData, readonly unknown[]>
        ): number | false => {
            const { data } = query.state
            const results = getResults(data)
            const hasPending = results.some(isPending)

            if (!hasPending || results.length === 0) {
                pollingStartedAtRef.current = null
                currentIntervalRef.current = intervalMs
                return false
            }

            const now = Date.now()
            if (pollingStartedAtRef.current === null) {
                pollingStartedAtRef.current = now
            }

            const elapsed = now - pollingStartedAtRef.current
            if (elapsed >= timeoutMs) {
                if (!pollTimeoutReachedRef.current) {
                    pollTimeoutReachedRef.current = true
                    setPollTimeoutReached(true)
                }
                return false
            }

            const delay = currentIntervalRef.current
            if (useBackOff) {
                currentIntervalRef.current = Math.min(
                    currentIntervalRef.current * (1 + backOffRate),
                    intervalMaxMs
                )
            }
            return delay
        },
        [
            getResults,
            isPending,
            intervalMs,
            timeoutMs,
            useBackOff,
            intervalMaxMs,
            backOffRate,
        ]
    )

    const reset = useCallback((): void => {
        pollingStartedAtRef.current = null
        pollTimeoutReachedRef.current = false
        currentIntervalRef.current = intervalMs
        setPollTimeoutReached(false)
    }, [intervalMs])

    return { refetchInterval, pollTimeoutReached, reset }
}
